(() => {
  if (window.FxrHomeModularLoaded) return;
  window.FxrHomeModularLoaded = true;
  const loadDeferredImage = (image) => {
    if (image.dataset.fxSrcset) {
      image.srcset = image.dataset.fxSrcset;
    }
  
    if (image.dataset.fxSrc) {
      image.src = image.dataset.fxSrc;
    }
  
    image.removeAttribute('data-fx-src');
    image.removeAttribute('data-fx-srcset');
  };
  
  const observeDeferredImages = (root = document) => {
    const images = root.querySelectorAll('.fx-deferred-image[data-fx-src]');
  
    if (!('IntersectionObserver' in window)) {
      images.forEach(loadDeferredImage);
      return;
    }
  
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          loadDeferredImage(entry.target);
          observer.unobserve(entry.target);
        });
      },
      {
        // Keep below-the-fold images out of the initial request burst so the
        // eager hero can finish discovery and decoding first.
        rootMargin: '300px 0px',
        threshold: 0.01,
      }
    );
  
    images.forEach((image) => observer.observe(image));
  };
  
  observeDeferredImages();
  document.addEventListener('shopify:section:load', (event) => observeDeferredImages(event.target));
  
  const updateApplicationCylinder = ({ track, cards, reduceMotion, geometry }) => {
    if (!track) return;
  
    const visibleCenter = track.scrollLeft + geometry.width / 2;
    const cylinderRadius = Math.max(geometry.width * 0.48, 1);
  
    cards.forEach((card, index) => {
      if (reduceMotion) {
        card.style.setProperty('--fxr-card-x', '0px');
        card.style.setProperty('--fxr-card-z', '0px');
        card.style.setProperty('--fxr-card-rotate', '0deg');
        card.style.setProperty('--fxr-card-scale', '1');
        card.style.setProperty('--fxr-card-opacity', '1');
        card.style.setProperty('--fxr-card-shadow', '.18');
        card.style.zIndex = '1';
        return;
      }
  
      const cardCenter = geometry.centers[index];
      const progress = Math.max(-1.15, Math.min(1.15, (cardCenter - visibleCenter) / cylinderRadius));
      const arcProgress = Math.min(Math.abs(progress), 1);
      const frontness = Math.cos(arcProgress * Math.PI / 2);
      const rotation = progress * -62;
      const translateX = Math.sign(progress) * Math.pow(arcProgress, 1.25) * -96;
      const translateZ = -300 + frontness * 350;
      const scale = 0.76 + frontness * 0.28;
      const opacity = 0.38 + frontness * 0.62;
      const shadow = 0.08 + frontness * 0.24;
  
      card.style.setProperty('--fxr-card-x', `${translateX.toFixed(2)}px`);
      card.style.setProperty('--fxr-card-z', `${translateZ.toFixed(2)}px`);
      card.style.setProperty('--fxr-card-rotate', `${rotation.toFixed(2)}deg`);
      card.style.setProperty('--fxr-card-scale', scale.toFixed(4));
      card.style.setProperty('--fxr-card-opacity', opacity.toFixed(4));
      card.style.setProperty('--fxr-card-shadow', shadow.toFixed(4));
      card.style.zIndex = String(Math.round(frontness * 100));
    });
  };
  
  const setupLoopingCarousel = (carousel, options) => {
      if (carousel.dataset[options.readyKey] === 'true') return;
  
      const track = carousel.querySelector(options.trackSelector);
      const previousButton = carousel.querySelector('[data-fxr-carousel-previous]');
      const nextButton = carousel.querySelector('[data-fxr-carousel-next]');
      const toggleButton = carousel.querySelector('[data-fxr-carousel-toggle]');
      const toggleIcon = toggleButton?.querySelector('span');
      const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      const interval = Number(carousel.dataset.interval) || 4000;
      const autoplayDirection = Number(options.autoplayDirection) < 0 ? -1 : 1;
      let manuallyPaused = reduceMotion || carousel.dataset.autoplay !== 'true';
      let isVisible = false;
      let animationFrame;
      let effectFrame;
      let moveTimer;
      let lastFrameTime;
      let resumeTimer;
      let isMoving = false;
  
      const stop = () => {
        window.cancelAnimationFrame(animationFrame);
        animationFrame = undefined;
        lastFrameTime = undefined;
        track?.classList.remove(options.autoClass);
      };
  
      const originalCards = track ? Array.from(track.children) : [];
  
      originalCards.forEach((card) => {
        const clone = card.cloneNode(true);
        clone.setAttribute(options.cloneAttribute, '');
        clone.setAttribute('aria-hidden', 'true');
        clone.setAttribute('inert', '');
        track.append(clone);
      });
  
      const effectCards = track ? Array.from(track.children) : [];
      let geometry = { width: 0, step: 0, loopDistance: 0, centers: [] };
      const measureGeometry = () => {
        if (!track) return;
        // Read layout together, before updating any card styles. Animation
        // frames then use cached geometry instead of forcing layout per card.
        const card = originalCards[0];
        const nextCard = originalCards[1] || effectCards[originalCards.length];
        const firstClone = effectCards[originalCards.length];
        const gap = nextCard ? 0 : Number.parseFloat(window.getComputedStyle(track).columnGap) || 0;
        geometry = {
          width: track.clientWidth,
          step: card && nextCard ? nextCard.offsetLeft - card.offsetLeft : (card?.offsetWidth || 0) + gap,
          loopDistance: card && firstClone ? firstClone.offsetLeft - card.offsetLeft : 0,
          centers: effectCards.map((item) => item.offsetLeft + item.offsetWidth / 2),
        };
      };
      measureGeometry();
      const getStep = () => geometry.step;
      const updateEffect = () => {
        if (!options.updateEffect || !track) return;
        options.updateEffect({ track, cards: effectCards, reduceMotion, geometry });
      };
      const requestEffectUpdate = () => {
        if (effectFrame !== undefined) return;
        effectFrame = window.requestAnimationFrame(() => {
          effectFrame = undefined;
          updateEffect();
        });
      };
  
      track?.addEventListener('scroll', requestEffectUpdate, { passive: true });
      const refreshGeometry = () => {
        measureGeometry();
        requestEffectUpdate();
      };
      window.addEventListener('resize', refreshGeometry, { passive: true });
      if ('ResizeObserver' in window && track) {
        const resizeObserver = new ResizeObserver(refreshGeometry);
        resizeObserver.observe(track);
        if (originalCards[0]) resizeObserver.observe(originalCards[0]);
      }
      updateEffect();
  
      observeDeferredImages(track || carousel);
  
      const getLoopDistance = () => geometry.loopDistance;
  
      const normalizePosition = () => {
        const loopDistance = getLoopDistance();
        if (!track || loopDistance <= 0) return;
  
        if (autoplayDirection < 0) {
          while (track.scrollLeft <= 0) {
            track.scrollLeft += loopDistance;
          }
  
          while (track.scrollLeft > loopDistance) {
            track.scrollLeft -= loopDistance;
          }
          return;
        }
  
        while (track.scrollLeft >= loopDistance) {
          track.scrollLeft -= loopDistance;
        }
  
        while (track.scrollLeft < 0) {
          track.scrollLeft += loopDistance;
        }
      };
  
      const setNavigationDisabled = (disabled) => {
        if (previousButton) previousButton.disabled = disabled;
        if (nextButton) nextButton.disabled = disabled;
      };
  
      const move = (direction) => {
        if (isMoving) return;
  
        if (!track || !carousel.isConnected) {
          stop();
          return;
        }
  
        const step = getStep();
        const loopDistance = getLoopDistance();
        if (!step || loopDistance <= 0) return;
  
        stop();
        isMoving = true;
        setNavigationDisabled(true);
  
        if (direction > 0 && track.scrollLeft >= loopDistance - step / 2) {
          track.scrollLeft -= loopDistance;
        }
  
        if (direction < 0 && track.scrollLeft <= step / 2) {
          track.scrollLeft += loopDistance;
        }
  
        track.scrollBy({ left: step * direction, behavior: reduceMotion ? 'auto' : 'smooth' });
        window.clearTimeout(moveTimer);
        moveTimer = window.setTimeout(() => {
          normalizePosition();
          isMoving = false;
          setNavigationDisabled(false);
          if (!carousel.contains(document.activeElement)) start();
        }, reduceMotion ? 0 : 480);
      };
  
      const animate = (timestamp) => {
        if (!track || !carousel.isConnected) {
          stop();
          return;
        }
  
        if (lastFrameTime === undefined) {
          lastFrameTime = timestamp;
        }
  
        const elapsed = Math.min(timestamp - lastFrameTime, 64);
        const step = getStep();
        lastFrameTime = timestamp;
  
        if (step > 0) {
          track.scrollLeft += (step * elapsed * autoplayDirection) / interval;
          normalizePosition();
        }
  
        animationFrame = window.requestAnimationFrame(animate);
      };
  
      const start = () => {
        stop();
        if (manuallyPaused || !isVisible || !track || document.hidden) return;
        if (autoplayDirection < 0 && track.scrollLeft <= 0) {
          const loopDistance = getLoopDistance();
          if (loopDistance > 0) track.scrollLeft = loopDistance;
        }
        track.classList.add(options.autoClass);
        animationFrame = window.requestAnimationFrame(animate);
      };
  
      const updateToggle = () => {
        if (!toggleButton || !toggleIcon) return;
        toggleIcon.textContent = manuallyPaused ? '▶' : 'Ⅱ';
        toggleButton.setAttribute(
          'aria-label',
          manuallyPaused ? toggleButton.dataset.playLabel : toggleButton.dataset.pauseLabel
        );
        toggleButton.setAttribute('aria-pressed', String(manuallyPaused));
      };
  
      previousButton?.addEventListener('click', () => move(-1));
      nextButton?.addEventListener('click', () => move(1));
      toggleButton?.addEventListener('click', () => {
        manuallyPaused = !manuallyPaused;
        updateToggle();
        start();
      });
  
      carousel.addEventListener('mouseenter', stop);
      carousel.addEventListener('mouseleave', start);
      carousel.addEventListener('focusin', stop);
      carousel.addEventListener('focusout', (event) => {
        if (!carousel.contains(event.relatedTarget)) start();
      });
      track?.addEventListener('pointerdown', () => {
        window.clearTimeout(resumeTimer);
        stop();
      });
      track?.addEventListener('pointerup', () => {
        window.clearTimeout(resumeTimer);
        resumeTimer = window.setTimeout(start, Math.min(interval, 1500));
      });
      track?.addEventListener('pointercancel', start);
      document.addEventListener('visibilitychange', () => document.hidden ? stop() : start());
  
      if ('IntersectionObserver' in window) {
        const visibilityObserver = new IntersectionObserver((entries) => {
          isVisible = entries[0]?.isIntersecting === true;
          if (isVisible) {
            start();
          } else {
            stop();
          }
        });
        visibilityObserver.observe(carousel);
      } else {
        isVisible = true;
        start();
      }
  
      carousel.dataset[options.readyKey] = 'true';
      updateToggle();
  };
  
  const loopingCarouselConfigs = [
    {
      rootSelector: '[data-fxr-product-carousel]',
      trackSelector: '.fxr-systems',
      itemSelector: '.fxr-system',
      cloneAttribute: 'data-fxr-carousel-clone',
      autoClass: 'is-auto-scrolling',
      readyKey: 'fxrCarouselReady',
      autoplayDirection: -1,
    },
    {
      rootSelector: '[data-fxr-application-carousel]',
      trackSelector: '.fxr-applications',
      itemSelector: '.fxr-application',
      cloneAttribute: 'data-fxr-application-clone',
      autoClass: 'is-auto-scrolling',
      readyKey: 'fxrApplicationCarouselReady',
      updateEffect: updateApplicationCylinder,
    },
  ];
  
  const setupLoopingCarousels = (root = document) => {
    loopingCarouselConfigs.forEach((options) => {
      root.querySelectorAll(options.rootSelector).forEach((carousel) => {
        if (carousel.dataset[options.readyKey] === 'true' || carousel.dataset.fxrSetupPending === 'true') return;
        if (!('IntersectionObserver' in window) || window.Shopify?.designMode) {
          setupLoopingCarousel(carousel, options);
          return;
        }
        carousel.dataset.fxrSetupPending = 'true';
        // Do not clone or measure carousels several screens below the hero.
        const setupObserver = new IntersectionObserver((entries) => {
          if (!entries.some((entry) => entry.isIntersecting)) return;
          setupObserver.disconnect();
          delete carousel.dataset.fxrSetupPending;
          setupLoopingCarousel(carousel, options);
        }, { rootMargin: '300px 0px' });
        setupObserver.observe(carousel);
      });
    });
  };
  
  const scheduleAfterInitialRender = (callback) => {
    const runWhenIdle = () => {
      if ('requestIdleCallback' in window) {
        window.requestIdleCallback(callback, { timeout: 1500 });
      } else {
        window.setTimeout(callback, 0);
      }
    };
  
    if (window.Shopify?.designMode) {
      callback();
      return;
    }
  
    if (document.readyState === 'complete') {
      runWhenIdle();
      return;
    }
  
    window.addEventListener('load', runWhenIdle, { once: true });
  };
  
  scheduleAfterInitialRender(() => setupLoopingCarousels());
  document.addEventListener('shopify:section:load', (event) => {
    observeDeferredImages(event.target);
    scheduleAfterInitialRender(() => setupLoopingCarousels(event.target));
  });
})();
